import { useState } from "react";
import { authedFetch } from "../../lib/api-client";
import { useT } from "../../lib/use-t";
import { SegmentedControl } from "./SegmentedControl";

export type DefaultModeRowProps = {
  /** Server value from `users.defaultModule`. Null → defaults to "spreken". */
  initialValue: "spreken" | "schrijven" | null;
};

/**
 * Backed Standaardmodus control — writes `defaultModule` via
 * POST /user/preferences (the field that feeds RootRedirect). Optimistic with
 * revert + error, mirroring DuoModeDefaultToggle.
 */
export function DefaultModeRow({ initialValue }: DefaultModeRowProps) {
  const t = useT();
  const [value, setValue] = useState<"spreken" | "schrijven">(initialValue ?? "spreken");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function set(next: string) {
    if (next !== "spreken" && next !== "schrijven") return;
    if (pending || next === value) return;
    const prev = value;
    setError(null);
    setValue(next);
    setPending(true);
    try {
      const res = await authedFetch("/user/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ defaultModule: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
    } catch {
      setValue(prev);
      setError(t("settings.practice.defaultModeSaveError"));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1.5">
      <SegmentedControl
        ariaLabel={t("settings.practice.defaultMode")}
        value={value}
        onChange={set}
        disabled={pending}
        options={[
          { value: "spreken", label: t("settings.practice.modeSpreken") },
          { value: "schrijven", label: t("settings.practice.modeSchrijven") },
        ]}
      />
      {error ? (
        <p role="alert" className="text-xs text-[var(--fail)]">
          {error}
        </p>
      ) : null}
    </div>
  );
}
